/**
 * Typo-tolerant query correction for storefront search.
 * Shopify's predictive/search APIs do exact token matching only, so this
 * fills the gap for near-misses against this store's own catalogue words.
 */
window.SearchSmart = (function () {
  // Known spelling/phrasing variants specific to this catalogue.
  var SYNONYMS = {
    tire: 'tyre',
    tires: 'tyres',
    rack: 'racking',
    racks: 'racking',
    shelf: 'shelving',
    shelves: 'shelving',
    'pump truck': 'pallet truck',
    'pump trucks': 'pallet trucks'
  };

  // Core category words, kept in sync with the collection handles in
  // snippets/rsi-search-allowed-collections.liquid, as a fuzzy-match target
  // in addition to the shop's live product types/vendors.
  var CATALOG_TERMS = [
    'shelving', 'racking', 'pallet', 'storage', 'boxes', 'tote', 'trucks',
    'warehouse', 'steps', 'ladders', 'tyre', 'benches', 'workbenches',
    'accessories', 'forklift', 'material', 'handling', 'decking', 'garage',
    'business', 'clearance', 'used', 'equipment', 'dispatch', 'bulk', 'deals'
  ];

  function levenshtein(a, b) {
    var m = a.length;
    var n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;

    var prevRow = [];
    var currRow = [];
    for (var j = 0; j <= n; j++) prevRow[j] = j;

    for (var i = 1; i <= m; i++) {
      currRow[0] = i;
      for (j = 1; j <= n; j++) {
        var cost = a[i - 1] === b[j - 1] ? 0 : 1;
        currRow[j] = Math.min(
          prevRow[j] + 1,
          currRow[j - 1] + 1,
          prevRow[j - 1] + cost
        );
      }
      prevRow = currRow.slice();
    }

    return prevRow[n];
  }

  function getVocabulary() {
    var fromTheme = (window.theme && window.theme.settings && window.theme.settings.searchVocabulary) || [];
    return CATALOG_TERMS.concat(fromTheme)
      .join(' ')
      .toLowerCase()
      .split(/[\s,/-]+/)
      .filter(function (word) {
        return word.length > 2;
      });
  }

  function correctWord(word) {
    var lower = word.toLowerCase();

    if (SYNONYMS[lower]) return SYNONYMS[lower];
    if (lower.length < 4 || /\d/.test(lower)) return null;

    var vocabulary = getVocabulary();
    if (vocabulary.indexOf(lower) !== -1) return null;

    var maxDistance = lower.length <= 5 ? 1 : 2;
    var best = null;
    var bestDistance = Infinity;

    for (var i = 0; i < vocabulary.length; i++) {
      var candidate = vocabulary[i];
      if (Math.abs(candidate.length - lower.length) > maxDistance) continue;

      var distance = levenshtein(lower, candidate);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
      }
    }

    return bestDistance <= maxDistance ? best : null;
  }

  /**
   * Attempts to correct typos/near-misses in a search query against this
   * store's catalogue vocabulary and known synonyms.
   * @param {string} query - The raw, as-typed search query.
   * @returns {?string} The corrected query, or null if no correction applies.
   */
  function correctQuery(query) {
    var words = query.trim().split(/\s+/);
    var changed = false;

    var corrected = words.map(function (word) {
      var fix = correctWord(word);
      if (fix && fix.toLowerCase() !== word.toLowerCase()) {
        changed = true;
        return fix;
      }
      return word;
    });

    return changed ? corrected.join(' ') : null;
  }

  /**
   * Rewrites a raw search query into a title-scoped, wildcard-suffixed query
   * for Shopify's classic full-text search (used by the /search results
   * page — NOT the predictive dropdown, which already ranks well on its own
   * via a wider explicit field list). Shopify's default /search relevance
   * scores by raw term-overlap without weighting rare/specific words over
   * common ones, so a distinctive word like "mesh" or "cantilever" can be
   * completely absent from results (buried behind generic matches on
   * "pallet"/"racking") while an unrelated blog post that merely mentions it
   * once ranks fine. Scoping every word to the product title field, with a
   * trailing wildcard so plurals/prefixes still match ("box*" still finds
   * "Boxes"), fixes both problems: it recovers real matching products that
   * were previously missing entirely, and it naturally excludes body-text-
   * only matches in unrelated content. Combined with the product-first
   * grouping in sections/main-search.liquid, this keeps results correct
   * even for queries that also happen to match an article/page title.
   * @param {string} query - The raw, as-typed search query.
   * @returns {?string} The scoped query, or null if the input was empty.
   */
  function buildScopedQuery(query) {
    var terms = query
      .trim()
      .split(/\s+/)
      .map(function (term) {
        // Strip characters that have special meaning in Shopify's search
        // query syntax so user input can't break or hijack the query.
        return term.replace(/["():*]/g, '');
      })
      .filter(function (term) {
        return term.length > 0;
      });

    if (!terms.length) return null;

    return terms
      .map(function (term) {
        return 'title:' + term + '*';
      })
      .join(' ');
  }

  return { correctQuery: correctQuery, buildScopedQuery: buildScopedQuery };
})();
